import { Link } from 'react-router'
import { type Route } from './+types/about.ts'

export const meta: Route.MetaFunction = () => {
	const title = 'About | DanceMakers'
	const description =
		"DanceMakers isn't just a convention or a competition—it's a movement built on heart. For over 40 years, we've believed dance is about more than steps and stages."
	const siteUrl = 'https://dancemakersinc.com'

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: `${siteUrl}/about` },
	]
}

export default function AboutRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section - Mission Statement */}
			<section className="container flex min-h-[calc(100vh-4rem)] items-center px-6 py-8 md:h-[calc(100vh-5rem)] md:px-16">
				<div className="grid w-full items-center gap-8 lg:grid-cols-2 lg:gap-16">
					{/* Image Column - First on mobile */}
					<div className="order-1 lg:order-2">
						<div className="relative">
							{/* Decorative accent */}
							<div className="absolute -inset-2 -z-10 bg-accent/10 md:-inset-3" />
							<img
								src="/img/about/erica-about.jpg"
								alt="The Taylor family - DanceMakers founders"
								className="h-auto w-full object-contain lg:aspect-[3/4] lg:max-h-[70vh] lg:object-cover"
							/>
						</div>
					</div>

					{/* Text Column */}
					<div className="order-2 lg:order-1">
						<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
							Our Story
						</p>
						<h1 className="mb-6 font-display text-2xl font-bold uppercase leading-tight md:text-3xl lg:text-4xl">
							A Movement Built on Heart
						</h1>

						<div className="space-y-4 text-sm text-muted-foreground">
							<p className="leading-relaxed">
								<span className="font-medium text-foreground">
									DanceMakers isn't just a convention or a competition
								</span>{' '}
								—{' '}
								<span className="font-medium text-foreground">
									it's a movement built on heart.
								</span>
							</p>

							<p className="leading-relaxed">
								For over 40 years, we've believed dance is about more than steps
								and stages. It's about belonging. About walking into a room and
								feeling seen, supported, and inspired to become more than you
								were when you arrived.
							</p>

							<p className="leading-relaxed">
								We teach technique, discipline, and artistry — but what we're
								really teaching is confidence, individuality, and love. Not
								chasing size or spectacle, but staying true to the values that
								started it all.
							</p>

							<p className="font-medium leading-relaxed text-foreground">
								Because it's never been about looking good. It's about making
								people feel worthy, empowered, and at home.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* ========================================
			    THE FULL STORY - Condensed Layout
			   ======================================== */}
			<section className="bg-muted/30 py-16 md:py-24">
				<div className="container px-6 md:px-16">
					<div className="grid gap-8 md:grid-cols-12 md:gap-12">
						{/* Sidebar Image - Sticky on desktop */}
						<div className="md:col-span-5">
							<div className="md:sticky md:top-28">
								<div className="relative">
									<div className="absolute -inset-2 -z-10 bg-accent/10 md:-inset-3" />
									<img
										src="/img/about/taylors.jpeg"
										alt="Brad and Felicia Taylor"
										className="aspect-[3/4] w-full object-cover"
									/>
								</div>
							</div>
						</div>

						{/* Main Text Content */}
						<div className="space-y-6 text-sm leading-relaxed text-muted-foreground md:col-span-7">
							<h2 className="mb-6 font-display text-2xl font-bold uppercase text-foreground md:text-3xl">
								The Legacy
							</h2>

							<p>
								DanceMakers, one of the first dance conventions to open its
								doors in 1978, was founded by Marjorie Perry, a visionary who
								sought to create a space where young dancers could not only
								perfect their craft but also connect with a larger community of
								artists and mentors. Perry's influence on the dance world was
								immeasurable, and under her stewardship, DanceMakers became a
								beloved institution, admired for both its commitment to
								excellence and its inclusive, family-like atmosphere. When Perry
								passed away in 2006, the company was at a crossroads — but it
								was also poised to embark on a new era, one shaped by the
								leadership of Doug Shaffer.
							</p>

							<p>
								Shaffer, who had spent over 20 years as the director of the
								Tremaine Dance Convention, brought with him an extensive
								understanding of the industry and a philosophy that would define
								the future of DanceMakers. "Doug was more than a mentor to us,"
								says Brad Taylor, who would go on to purchase DanceMakers with
								his wife, Felicia, in 2014. "He was a teacher in every sense of
								the word — someone who understood the dance world inside and
								out, but who also understood that what you create goes far
								beyond the steps you teach." Shaffer's approach to DanceMakers
								was rooted in a commitment to mentorship, not just technique. He
								understood that a dance convention could serve as a place for
								young artists to find their voice, to be inspired, and to
								connect with others who shared their passion.
							</p>

							<p>
								Brad Taylor's relationship with DanceMakers began long before he
								became its owner. As a young dancer, he attended the convention
								as a student, captivated by the energy and the opportunity to
								learn from some of the industry's best. At 14, he began
								assisting faculty members at events across the country, slowly
								but surely becoming part of the company's fabric. In 2005, at
								the age of 21, Brad was invited to join the faculty as a hip-hop
								teacher — a role that would shape not only his career but his
								relationship with the company. "It wasn't just about teaching
								dance," Brad reflects. "It was about creating a space where
								dancers felt supported, where they felt seen. I wanted to
								contribute to that energy."
							</p>

							<p>
								It was during these early years that Brad met Felicia, his
								girlfriend at the time, who would later become his wife. "I
								didn't want to sit on the sidelines," Felicia remembers. "I was
								always the one jumping in, helping with whatever needed to be
								done. If there was a task, I was doing it." Together, the couple
								became an integral part of DanceMakers' success, with Felicia
								taking on the role of Tour Director and Brad becoming Creative
								Director. By 2008, after graduating from college, they moved to
								California, ready to work behind the scenes to ensure the
								convention's continued growth.
							</p>

							<p>
								Their deep connection to DanceMakers was not just about their
								roles but about the larger mission of the company. In 2014, they
								took the monumental step of purchasing the company from Doug
								Shaffer. "We didn't want to be just owners," Brad says. "We
								wanted to be stewards of something that's bigger than us —{' '}
								something that has been built over decades by so many incredible
								people. It's important to us that the values of this company
								stay intact." For the Taylors, DanceMakers is not a personal
								project but a legacy that they are committed to nurturing and
								passing on to the next generation of leaders.
							</p>

							<p>
								As the stewards of DanceMakers, the Taylors have always been
								focused on the larger purpose of their work. "We don't train
								dancers in three days," Brad explains. "It's impossible to train
								a dancer in such a short time. What we do is create an
								environment that inspires dancers to go back to their home
								studios and continue their education. We want to provide a space
								that encourages them to push themselves, to grow, and to find
								confidence in who they are." Felicia adds, "It's so much more
								than dance. It's about giving these young people the confidence
								to stand in their own skin, to be proud of who they are, and to
								feel inspired not just by the faculty, but by the other dancers
								in the room."
							</p>

							<p>
								The Taylors' commitment to preserving the spirit of DanceMakers
								while continuing its evolution has become the hallmark of their
								leadership. "It wasn't one thing that made DanceMakers what it
								is," Brad reflects. "It was a group of people, all aligned with
								the same vision. Over time, we've found our tribe — our
								community of dancers, teachers, and families who share our
								passion for this work." That sense of belonging and connection
								is what, in the end, makes DanceMakers what it is — a space
								where people feel seen, valued, and loved.
							</p>

							<p>
								Though the Taylors have never sought to be the biggest dance
								convention, they recognize that it's time to expand the tour
								into new markets. This year, they are adding a dozen new cities
								across the country, eager to share the energy and spirit of
								DanceMakers with even more dancers. "We feel that like energies
								attract like energies," Felicia says. "And that makes it so
								exciting to bring in new friends, new members of our dance
								family. Each new city is an opportunity to meet people who are
								aligned with our values, who want to be part of this amazing
								community."
							</p>

							<p>
								For the Taylors, the expansion of DanceMakers is not about
								achieving dominance in the industry; it's about continuing to
								grow the family. "It's about bringing more people into the
								tribe, helping them find their place, and inspiring them to grow
								in ways they might never have imagined," Brad says. "That's the
								heart of DanceMakers."
							</p>

							<p>
								As they look toward the future, Brad and Felicia remain
								steadfast in their commitment to preserving the values that have
								made DanceMakers a beloved institution for over four decades.
								"The legacy of this company isn't ours," Brad says. "It's the
								people who have come through these doors — the dancers, the
								teachers, the families who have made DanceMakers what it is.
								We're just here to help guide that legacy until it's time for
								the next generation to take it on."
							</p>

							<p className="pt-4 font-medium text-foreground">
								In this way, DanceMakers continues to be what it was always
								meant to be: not just a dance convention, but a place of
								connection, growth, and inspiration. And for the Taylors, that's
								the greatest legacy they can offer.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Each tour is a chapter in our story */}
			<section className="py-16 md:py-24">
				<div className="container mx-auto max-w-2xl px-6 text-center md:px-16">
					<h2 className="mb-2 font-display text-2xl font-bold uppercase text-foreground md:text-3xl">
						Each tour is a chapter in our story
					</h2>
					<p className="mb-6 text-sm leading-relaxed text-primary">
						We invite you to help write the{' '}
						<Link
							to="/tour"
							className="text-primary underline transition-colors hover:text-primary/80"
						>
							next
						</Link>
						.
					</p>
				</div>
			</section>
		</div>
	)
}
